import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { RouterPaths } from 'src/components/Router/Router.constants';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import {
  alertBodyKeys,
  canAcknowledge,
  withActionableCta,
} from 'src/alerts/AlertCenter.functions';
import type { tLiveAlert } from 'src/alerts/AlertCenter.types';
import { useAlertCenterContext } from 'src/providers/AlertCenterProvider/AlertCenter.hook';
import { useFeatureFlagsContext } from 'src/providers/FeatureFlagsProvider/FeatureFlags.context';
import { useNotificationsContext } from 'src/providers/NotificationProvider/Notifications.constants';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import {
  renderableNotifications,
  resolveNotificationCopy,
} from 'src/notifications/AgendaNotifications.functions';
import type { tGenericNotification } from 'src/notifications/AgendaNotifications.types';
import { ALERT_TIER_ICON, NOTIFICATION_ICON } from './AlertCenter.icons';
import { useAlertCta } from './AlertCenter.cta.hook';
import { parseLeadChatUrl } from '../ChatModal/LeadChat.functions';
import LeadChatLink from '../ChatModal/LeadChatLink.component';
import { useAlertPaywallExposureAnalytics } from './AlertCenter.exposure.hook';
import {
  acknowledgeableAlerts,
  partitionPanelSections,
  resolvePanelAlignment,
} from './AlertPanel.functions';
import { useFocusTrap } from './AlertPanel.hook';
import type {
  tAlertPanelProps,
  tAlertPanelSectionId,
} from './AlertPanel.types';
import './AlertPanel.styles.scss';

/**
 * The Alert Center panel — three semantic sections (M5): criticals waiting
 * for the wide strip, pending decisions (P1), and news (P3).
 *
 * MOUNTED conditionally by `AlertBell`, never merely hidden. A CSS-hidden
 * component still mounts and still runs every hook it owns; INT-834 is the
 * repo's record of an always-mounted drawer freezing workspaces for five
 * days. Everything here — the focus trap, the acknowledge seams — is
 * therefore scoped to "this component exists".
 */
export default function AlertPanel(props: tAlertPanelProps) {
  const { t } = useTranslation(ALERT_CENTER_NS);
  const { alerts, waitingCriticalAlerts, acknowledgeAlert } =
    useAlertCenterContext();
  const { currentNotifications, closeNotification } = useNotificationsContext();
  const { currentUserRole } = useUserContext();
  const role = currentUserRole ?? null;
  // RET-1956 — a `detail` CTA opens the SAME detail the row's text area opens
  // (AlertBell owns it), so the panel injects that opener instead of hosting a
  // second one.
  const runCta = useAlertCta({
    onOpenDetail: props.onOpenDetail,
    surface: 'bell',
  });
  const navigate = useNavigate();
  const { isAlertCenterPageEnabled } = useFeatureFlagsContext();

  const [alignment, setAlignment] = useState<'right' | 'left'>('right');
  const containerRef = useRef<HTMLDivElement>(null);

  useFocusTrap({ containerRef, onEscape: props.onClose });

  // Runs once on mount: the bell's horizontal position is layout-dependent
  // (navbar right cluster vs. Home's left-side switcher row), so `right-0`
  // can only be trusted after measuring how much room actually exists.
  useLayoutEffect(() => {
    const panelLeft = containerRef.current?.getBoundingClientRect().left ?? 0;
    setAlignment(resolvePanelAlignment({ panelLeft }));
  }, []);

  const sections = useMemo(
    () => partitionPanelSections({ waitingCriticalAlerts, alerts }),
    [waitingCriticalAlerts, alerts],
  );
  // AGI-346 — `markable` feeds "mark all as seen" and stays ALERTS ONLY. The
  // tray's control is a dismiss that POSTs and empties the inbox for good, so
  // sweeping it from a header button would delete the record in one click —
  // the exact failure this ticket exists to remove.
  const markable = useMemo(
    () => acknowledgeableAlerts({ alerts, role }),
    [alerts, role],
  );

  const notifications = useMemo(
    () => renderableNotifications(currentNotifications),
    [currentNotifications],
  );

  const isEmpty =
    sections.waiting.length === 0 &&
    sections.decisions.length === 0 &&
    sections.news.length === 0 &&
    // Without this the tray is unreachable in the common case: a workspace
    // with appointments and no live alert would render the empty state over
    // its own notifications.
    notifications.length === 0;

  // RET-1891 — the panel is the ONLY surface the two `slot` paywall alerts
  // reach, so this mount IS their exposure: the component exists only while
  // the user has the bell open (KTD14), and the list it hands over is what is
  // rendered below, so an alert arriving under an open panel counts too.
  const listedAlerts = useMemo(
    () => [...sections.waiting, ...sections.decisions, ...sections.news],
    [sections],
  );
  useAlertPaywallExposureAnalytics(listedAlerts);

  const markAll = () => markable.forEach((alert) => acknowledgeAlert(alert));

  const renderRow = (alert: tLiveAlert, section: tAlertPanelSectionId) => {
    const Icon = ALERT_TIER_ICON[alert.priority];
    const { copy } = withActionableCta(alert);
    const title = t(alert.copy.titleKey, alert.copy.values ?? {});
    const bodyKeys = alertBodyKeys(alert);
    // G11 — a waiting critical row never carries a dismissal control (R13:
    // P0s resolve, they are not acknowledged), and shows its resolution
    // window so the user knows what waiting costs. `canAcknowledge` would
    // already refuse a P0, but the section decides the CONTROL SET, not the
    // tier: rows here render window + CTA, nothing else.
    const isWaiting = section === 'waiting';
    const isAcknowledgeable =
      !isWaiting && role ? canAcknowledge(alert, role) : false;

    return (
      <li
        key={alert.key}
        className={classNames('AlertPanel__Row', `AlertPanel__Row--${alert.priority}`)}
        data-testid={`alert-row-${alert.id}`}
        data-priority={alert.priority}
        /*
         * U12 (KTD12) — the panel is a DELIVERY SURFACE for prominence, not
         * just a list. Only `AlertInPage` stamped this attribute, and the
         * ladder's yellow rung renders in the SLOT for days 0-4 — which is 76%
         * of recoveries — so without the stamp the palette had nowhere to
         * land. The id rides along because the overrides are keyed per family,
         * the way `AlertInPage.styles.scss` already keys its own: `slot` is
         * shared with ~17 declarations and a blanket attribute rule would
         * repaint every one of them.
         */
        data-alert-id={alert.id}
        data-prominence={alert.prominence ?? undefined}
      >
        <div className="AlertPanel__RowMain">
          <Icon className="AlertPanel__RowIcon" aria-hidden="true" />

          {/* U4 — the text area is the door to the detail modal: a real
              button (Tab + Enter/Space), passing itself along so focus can
              come back to this exact row when the modal closes. */}
          <button
            type="button"
            className="AlertPanel__RowOpen"
            data-testid={`alert-row-open-${alert.id}`}
            onClick={(event) => props.onOpenDetail(alert, event.currentTarget)}
          >
            <span className="AlertPanel__RowText">
              {/* KTD7 — free-wrapping (product decision 2026-08-21,
                  RET-1900): the full text is shown here, so the row carries
                  no `title` tooltip duplicating what is already on screen.
                  The panel scrolls (`max-h-96`); the detail modal stays for
                  the RICH renderers, not as the only path to the words. */}
              <span className="AlertPanel__RowTitle">{title}</span>
              {bodyKeys.length > 0 && (
                <span
                  className="AlertPanel__RowBody"
                  data-testid={`alert-row-body-${alert.id}`}
                >
                  {/* U12 — one LINE per independent fact; a single-body family
                      renders exactly what it always did.
                      Two account-health bodies carry <0>…</0> markup; t() alone
                      would print the tags. The anchor degrades to emphasis
                      exactly as it does elsewhere: the row's CTA already
                      carries the action, and a second competing target inside
                      the row's text is not one anyone aims at. */}
                  {bodyKeys.map((bodyKey) => (
                    <span key={bodyKey} className="AlertPanel__RowBodyLine">
                      <Trans
                        t={t}
                        i18nKey={bodyKey}
                        values={alert.copy.values ?? {}}
                        components={[<span key="0" className="font-semibold" />]}
                      />
                    </span>
                  ))}
                </span>
              )}
              {isWaiting && (
                <span
                  className="AlertPanel__RowWindow"
                  data-testid={`alert-row-window-${alert.id}`}
                >
                  {t(`panel.waiting.window.${alert.id}`)}
                </span>
              )}
            </span>
          </button>
        </div>

        {/* Actions live UNDER the text, never beside it: two nowrap buttons
            sharing the text's row is what squeezed it into one word per line
            (the KTD7 collapse — see AlertPanel.styles.scss). */}
        {(copy.cta || isAcknowledgeable) && (
          <div className="AlertPanel__RowActions">
            {copy.cta && (
              <button
                type="button"
                className="AlertPanel__RowCta"
                data-testid={`alert-row-cta-${alert.id}`}
                // The button itself travels as the invoker: a `detail` CTA
                // returns focus here on close, not to the row's text area.
                onClick={(event) => runCta(alert, event.currentTarget)}
              >
                {t(copy.cta.labelKey)}
              </button>
            )}
            {isAcknowledgeable && (
              <button
                type="button"
                className="AlertPanel__RowAck"
                data-testid={`alert-row-ack-${alert.id}`}
                onClick={() => acknowledgeAlert(alert)}
              >
                {t(alert.priority === 'p3' ? 'panel.dismiss' : 'panel.acknowledge')}
              </button>
            )}
          </div>
        )}
      </li>
    );
  };

  /**
   * AGI-346 — a tray row. It shares the alert row's classes so both read as
   * one list, and differs in exactly the two ways that matter: no
   * `--p{tier}` tint, because there is no severity to encode, and one closing
   * control instead of two, because "I saw it" and "clear it from my inbox"
   * are the same act for a notification.
   *
   * No detail modal either: an alert row opens one because it carries a
   * decision worth a full screen. "Damo booked Thursday" is the whole story.
   */
  const renderNotificationRow = (notification: tGenericNotification) => {
    const copy = resolveNotificationCopy(notification);
    // `renderableNotifications` already dropped these, so this is a type
    // narrowing rather than a branch anyone reaches.
    if (!copy) return null;

    const Icon = NOTIFICATION_ICON[copy.icon];
    const values = {
      ...copy.values,
      name: copy.values.name || t('agenda.leadFallback'),
    };
    const cta = copy.cta;
    // A conversation CTA opens the chat popup over the current page (AGI-761).
    const chatTarget = cta ? parseLeadChatUrl(cta.target, window.location.origin) : null;

    return (
      <li
        key={notification.id}
        className="AlertPanel__Row AlertPanel__Row--notification"
        data-testid={`notification-row-${notification.id}`}
        data-event={notification.event}
      >
        <div className="AlertPanel__RowMain">
          <Icon className="AlertPanel__RowIcon" aria-hidden="true" />
          <span className="AlertPanel__RowText">
            <span className="AlertPanel__RowTitle">{t(copy.titleKey)}</span>
            <span className="AlertPanel__RowBody">
              {t(copy.bodyKey, values)}
            </span>
          </span>
        </div>
        <div className="AlertPanel__RowActions">
          {cta && chatTarget && (
            <LeadChatLink
              chat={chatTarget}
              className="AlertPanel__RowCta"
              data-testid={`notification-row-cta-${notification.id}`}
              onClick={props.onClose}
            >
              {t(cta.labelKey)}
            </LeadChatLink>
          )}
          {cta && !chatTarget && (
            <button
              type="button"
              className="AlertPanel__RowCta"
              data-testid={`notification-row-cta-${notification.id}`}
              onClick={() => {
                navigate(cta.target);
                props.onClose();
              }}
            >
              {t(cta.labelKey)}
            </button>
          )}
          <button
            type="button"
            className="AlertPanel__RowAck"
            data-testid={`notification-row-dismiss-${notification.id}`}
            onClick={() => closeNotification(notification.id)}
          >
            {t('panel.dismiss')}
          </button>
        </div>
      </li>
    );
  };

  const renderAgendaSection = () => {
    if (notifications.length === 0) return null;
    return (
      <section
        className="AlertPanel__Section AlertPanel__Section--agenda"
        data-testid="alert-panel-section-agenda"
        aria-labelledby="alert-panel-section-heading-agenda"
      >
        <h3
          className="AlertPanel__SectionTitle"
          id="alert-panel-section-heading-agenda"
        >
          {t('panel.sections.agenda')}
        </h3>
        <ul className="AlertPanel__Rows">
          {notifications.map(renderNotificationRow)}
        </ul>
      </section>
    );
  };

  const renderSection = (section: tAlertPanelSectionId) => {
    const sectionAlerts = sections[section];
    if (sectionAlerts.length === 0) return null;
    return (
      <section
        key={section}
        className={classNames(
          'AlertPanel__Section',
          `AlertPanel__Section--${section}`,
        )}
        data-testid={`alert-panel-section-${section}`}
        aria-labelledby={`alert-panel-section-heading-${section}`}
      >
        <h3
          className="AlertPanel__SectionTitle"
          id={`alert-panel-section-heading-${section}`}
        >
          {t(`panel.sections.${section}`)}
        </h3>
        <ul className="AlertPanel__Rows">
          {sectionAlerts.map((alert) => renderRow(alert, section))}
        </ul>
      </section>
    );
  };

  return (
    <div
      ref={containerRef}
      className={classNames('AlertPanel', {
        'AlertPanel--flipLeft': alignment === 'left',
      })}
      role="dialog"
      aria-label={t('panel.title')}
      data-testid="alert-panel"
      tabIndex={-1}
    >
      <div className="AlertPanel__Header">
        <p className="AlertPanel__Title">{t('panel.title')}</p>
        <div className="AlertPanel__HeaderActions">
          {markable.length > 0 && (
            <button
              type="button"
              className="AlertPanel__MarkAll"
              data-testid="alert-panel-mark-all"
              onClick={markAll}
            >
              {t('panel.markAll')}
            </button>
          )}
          <button
            type="button"
            className="AlertPanel__Close"
            aria-label={t('panel.close')}
            data-testid="alert-panel-close"
            onClick={props.onClose}
          >
            <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {isEmpty ? (
        <p className="AlertPanel__Empty" data-testid="alert-panel-empty">
          {t('panel.empty')}
        </p>
      ) : (
        <div className="AlertPanel__List">
          {(['waiting', 'decisions', 'news'] as tAlertPanelSectionId[]).map(
            renderSection,
          )}
          {/* Last on purpose: alerts ask the user to do something, the tray
              only tells them what happened. Reading order follows that. */}
          {renderAgendaSection()}
        </div>
      )}

      {/* AGI-284 — the panel is the glance, the Alert Center page is the
          desk: suggestions live only there, and long lists read better on a
          full page. Shown in the empty state too — "nothing urgent" is not
          "nothing to see". Behind the page's own per-workspace rule (the one
          the route and the sidebar entry follow, AGI-893): a door to a page
          the workspace cannot enter is worse than no door. */}
      {isAlertCenterPageEnabled && (
        <button
          type="button"
          className="AlertPanel__SeeAll"
          data-testid="alert-panel-see-all"
          onClick={() => {
            navigate(RouterPaths.alertCenter);
            props.onClose();
          }}
        >
          {t('panel.seeAll')}
        </button>
      )}
    </div>
  );
}
